import { useCallback, useState } from "react";
import { types } from "@vilocnv/allsetra-core";
import ObjectsService from "app/data/services/ObjectsService";
import { isValidMapCoordinate } from "app/data/helpers/mapHelpers";
import { ObjectRideModeEnum } from "app/data/helpers/objectRideHelpers";
import { getLastTripsSearchParams } from "../constants/lastTrips.constants";

type MigratedRide = types.IObjectRide & {
  isMigrated?: boolean;
  correctedStartLatitude?: number | null;
  correctedStartLongitude?: number | null;
  correctedEndLatitude?: number | null;
  correctedEndLongitude?: number | null;
};

const tripHasValidStartEnd = (trip: MigratedRide) => {
  const startLat = trip.correctedStartLatitude ?? trip.startLatitude;
  const startLng = trip.correctedStartLongitude ?? trip.startLongitude;
  const endLat = trip.correctedEndLatitude ?? trip.endLatitude;
  const endLng = trip.correctedEndLongitude ?? trip.endLongitude;

  return (
    isValidMapCoordinate(startLat, startLng) &&
    isValidMapCoordinate(endLat, endLng)
  );
};

export function useLastTrips(accountId: string | undefined) {
  const [trips, setTrips] = useState<types.IObjectRide[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [selectedTrip, setSelectedTrip] = useState<MigratedRide | null>(null);
  const [rideLocations, setRideLocations] = useState<
    types.IObjectRideLocation[]
  >([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState<string | null>(null);

  const fetchLastTrips = useCallback(
    async (objectId: string) => {
      if (!accountId || !objectId) return;

      setLoading(true);
      setError(null);
      setTrips([]);
      setSelectedTripId(null);
      setSelectedTrip(null);
      setRideLocations([]);
      setLocationsError(null);

      try {
        const response = await ObjectsService.getObjectRidesByQuery(
          { accountId, objectId },
          getLastTripsSearchParams()
        );

        if (response.status === 200) {
          setTrips(response.data?.results ?? []);
        } else {
          setError("fetch_failed");
        }
      } catch {
        setError("fetch_failed");
      } finally {
        setLoading(false);
      }
    },
    [accountId]
  );

  const selectTrip = useCallback(
    async (trip: types.IObjectRide, objectId: string) => {
      if (!accountId) return;

      setSelectedTripId(trip.uniqueId);
      setSelectedTrip(trip as MigratedRide);
      setRideLocations([]);

      if (trip.rideMode === ObjectRideModeEnum.PRIVATE) {
        setLocationsLoading(false);
        setLocationsError("private_ride");
        return;
      }

      setLocationsLoading(true);
      setLocationsError(null);

      try {
        const response = await ObjectsService.getObjectRideLocations({
          accountId,
          objectId,
          rideId: trip.uniqueId
        });

        if (response.status === 200) {
          const locations: types.IObjectRideLocation[] = response.data ?? [];
          const migratedTrip = trip as MigratedRide;
          const validLocations = locations.filter((item) =>
            isValidMapCoordinate(item.latitude, item.longitude)
          );
          const hasStartEnd = tripHasValidStartEnd(migratedTrip);

          if (validLocations.length < 2 && !hasStartEnd) {
            setLocationsError("no_route_data");
          }

          setRideLocations(locations);
        } else {
          setLocationsError("fetch_failed");
        }
      } catch {
        setLocationsError("fetch_failed");
      } finally {
        setLocationsLoading(false);
      }
    },
    [accountId]
  );

  const reset = useCallback(() => {
    setTrips([]);
    setLoading(false);
    setError(null);
    setSelectedTripId(null);
    setSelectedTrip(null);
    setRideLocations([]);
    setLocationsLoading(false);
    setLocationsError(null);
  }, []);

  return {
    trips,
    loading,
    error,
    selectedTripId,
    selectedTrip,
    rideLocations,
    locationsLoading,
    locationsError,
    fetchLastTrips,
    selectTrip,
    reset
  };
}
